import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSharedState } from "state";
import PageLayout from "components/layout/page-layout";
import moment from "moment";
import Table, { tableHeader } from "components/table";
import { oneLine } from "components/table/table-parts";
import ActionButton from "components/table/action-btn";
import { IconFolderOpen } from "@tabler/icons-react";
import { getBudgetRequests } from "api/budget_request";

export default function Budgets() {
  const {
    budgetRequests,
    setBudgetRequests,
    getBudgetRequestsData,
    searchQuery,
    handleSearch,
    setOffset,
    firstEntry,
    filterQuery,
    filtersCount,
    user,
    companies,
    getCompaniesData,
    updateFirstEntry,
  } = useSharedState();

  const navigate = useNavigate();
  const { locationName } = useParams();
  const [localBudgetRequests, setLocalBudgetRequests] = useState(budgetRequests);

  const selectedLocation = user.locations?.find((loc) => loc.location === locationName);

  useEffect(() => {
    setLocalBudgetRequests(budgetRequests);
    setBudgetRequests(budgetRequests);
  }, [budgetRequests]);

  // Handle location-based filtering
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (locationName && selectedLocation) {
          // Ensure companies data is loaded
          if (companies.length === 0) {
            await getCompaniesData();
          }

          // Find companies for this location
          const locationCompanies = companies.filter((company) => company.location_id === selectedLocation.location_id);

          if (locationCompanies.length > 0) {
            // Use the first company for this location (or you could aggregate multiple companies)
            const companyId = locationCompanies[0].company_id;
            const params = new URLSearchParams();
            params.append("company_id", companyId.toString());

            const res = await getBudgetRequests(Object.fromEntries(params.entries()));
            setLocalBudgetRequests(res);
            setBudgetRequests(res);
          } else {
            // No companies found for this location, show empty results
            setLocalBudgetRequests({ total_count: 0, budget_requests: [] });
            setBudgetRequests({ total_count: 0, budget_requests: [] });
          }
        }
        setOffset(0);
        updateFirstEntry(1);
      } catch (error: any) {
        console.error("Error fetching budget data:", error);
      }
    };

    fetchData();
  }, [locationName, companies]);

  const getFilterParams = () => {
    if (!filterQuery) return undefined;
    const params = new URLSearchParams();
    Object.entries(filterQuery).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        params.append(key, value.toString());
      }
    });
    return params;
  };

  const updateBudgetsList = async () => {
    const params = getFilterParams();
    await getBudgetRequestsData(undefined, params);
  };

  const onBudgetPageChange = async (offset: number) => {
    setOffset(offset);
    updateFirstEntry(offset + 1);

    try {
      if (locationName && selectedLocation) {
        // Find companies for this location
        const locationCompanies = companies.filter((company) => company.location_id === selectedLocation.location_id);

        if (locationCompanies.length > 0) {
          const companyId = locationCompanies[0].company_id;
          const params = { company_id: companyId, offset };

          const res = await getBudgetRequests(params);
          setLocalBudgetRequests(res);
          setBudgetRequests(res);
        }
      }
    } catch (error: any) {
      console.error("Error fetching paginated budget data:", error);
    }
  };

  const budgetsTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Год", narrow: false, wrap: false },
    { header_text: "Компания", narrow: false, wrap: false },
    { header_text: "Отдел", narrow: false, wrap: false },
    { header_text: "Бюджет", narrow: false, wrap: false },
    { header_text: "Дата БО", narrow: false, wrap: false },
    { header_text: "Создатель", narrow: false, wrap: false },
    { header_text: "Действия", narrow: true, wrap: false },
  ];

  const budgetsList = (
    <>
      {localBudgetRequests.budget_requests.map((budget, index) => (
        <tr key={index}>
          <td>{oneLine((firstEntry + index).toString())}</td>
          <td>{oneLine(budget.year.toString() || "-")}</td>
          <td>{oneLine(budget.company || "-")}</td>
          <td>{oneLine(budget.department || "-")}</td>
          <td>{oneLine(budget.budget_type || "-")}</td>
          <td>{oneLine(moment(budget.request_date).format("DD.MM.YYYY") || "-")}</td>
          <td>{oneLine(budget.creator_name || "-")}</td>
          <td>
            <div className="btn-list flex-nowrap">
              <ActionButton
                icon={<IconFolderOpen className="icon" />}
                title="Подробнее"
                action={() => navigate(`${budget.budget_request_id}`)}
                color={"primary"}
                modal={false}
              />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  const budgetsTableComponent = Table(budgetsTableHeaders, budgetsList, true, {
    total: localBudgetRequests.total_count,
    updateInfo: onBudgetPageChange,
  });

  const getHeaderText = () => {
    if (selectedLocation) {
      return `Бюджеты - ${selectedLocation.location}`;
    }
    return "Бюджеты";
  };

  return (
    <PageLayout
      children={budgetsTableComponent}
      header={getHeaderText()}
      subheaderText={`Количество бюджетов: ${localBudgetRequests.total_count}`}
      search={false}
      filters={true}
      buttonText={"Новый бюджет"}
      buttonAction={() => navigate("create")}
    />
  );
}
